import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import { Lock, Security } from "@mui/icons-material";
import { useVerificationCode } from "../../../signup/accountVerify/hooks/useVerificationCode";
import { SnackbarAlert } from "../../../common/SnackbarAlert";
import { useVaultLogic } from "../../hooks/useVaultLogic";
import { useModals } from "../../../../context/ModalContext"; 
import formatMMSS from "../helpper/formatMMSS";
import { colors, jobColors } from "../../../ui/colors";
type Props = {
  id: string;
  itemTitle: string;
  emailMasked: string;
  // Cambia esto para que acepte el segundo argumento opcional
  onVerified: (data: any, code?: string) => void; 
  vaultRemainingSeconds?: number;
  modalKey: string;
}

/**
 * Refactored:
 * - Removes useTwoFa hook usage.
 * - Uses the vault backend flow:
 *   1) GET /passwords/:id          -> sends 2FA email (202 requires2FA)
 *   2) GET /passwords/:id?code=XXX -> verifies 2FA and returns decrypted record (200)
 */
export default function VerificationModal({ id, itemTitle, emailMasked, onVerified, modalKey,
  vaultRemainingSeconds}: Props) {
  const DIGITS = 5;

console.log("VerificationModal render");

const { modals, closeModal } = useModals();

  const closeThisModal = React.useCallback(() => {
    const candidates = (modals || []).filter((m: any) => m.key === modalKey && !m.minimized);
    if (!candidates.length) return;
    closeModal(candidates[candidates.length - 1].id);
  }, [modals, closeModal, modalKey]);

  const { code, inputsRef, handleChange, handleKeyDown, handlePaste, getCodeValue, isCodeComplete } =
    useVerificationCode(DIGITS);

  const { sendTwoFaTrigger } = useVaultLogic(true);

  const [isLoading, setIsLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "info" | "warning" | "error">("error");

  // Track per-id sends to prevent loops / re-mounts / unstable deps
  const hasSentRef = useRef<Record<string, boolean>>({});

  // Keep latest function without putting it in deps
  const sendFnRef = useRef(sendTwoFaTrigger);
  
  useEffect(() => {
    sendFnRef.current = sendTwoFaTrigger;
  }, [sendTwoFaTrigger]);

  useEffect(() => {
    const codeValue = getCodeValue();
    const run = async () => {
      if (hasSentRef.current[id]) return;
      hasSentRef.current[id] = true;

      setIsLoading(true);
      try {
        const r: any = await sendFnRef.current({ id});

        if (r && "requires2FA" in r) {
          setAlertMessage(r.message ?? "A verification code has been sent to your email.");
          setAlertSeverity("info");
          setOpenAlert(true);
        } 
        
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Error sending 2FA code";
        setAlertMessage(msg);
        setAlertSeverity("error");
        setOpenAlert(true);

        // allow retry if sending failed
        hasSentRef.current[id] = false;
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [id]);

 const handleSubmit = async () => {
  if (!isCodeComplete || isLoading) return;
  setIsLoading(true);
  try {
    const codeValue = getCodeValue();
    const r: any = await sendTwoFaTrigger({ id, code: codeValue });

    if (r && typeof r === "object" && "requires2FA" in r) {
      setAlertMessage("Code incorrect");
      setAlertSeverity("error");
      setOpenAlert(true);
      return;
    }

    
    onVerified(r, codeValue);

  } catch (err: any) {
    
    const backendMessage = err?.response?.data?.message || "Error verifying code";
    setAlertMessage(backendMessage);
    setAlertSeverity("error");
    setOpenAlert(true);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 750,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" }, 
        overflow: "hidden",
        borderRadius: "12px",
      }}
    >
      
      <Box
        sx={{
          width: { xs: "100%", sm: "40%" },
          background: "linear-gradient(180deg, var(--fx-surface-muted, #F1F5F9) 0%, #E5E7EB 100%)",
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          borderRight: "1px solid var(--fx-surface-border, #E2E8F0)"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
          <Lock sx={{ fontSize: 20, backgroundColor: colors.orange.base, color: "white", borderRadius: "6px", p: "3px" }} />
          <Typography variant="caption" fontWeight={800} sx={{ color: "var(--fx-surface-textsecondary, #64748B)", letterSpacing: 1, textTransform: 'uppercase' }}>
            Vault Guard
          </Typography>
        </Box>

        <Box
          sx={{
            width: 100,
            height: 100,
            backgroundColor: "#FED7AA",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
            boxShadow: "0 4px 20px rgba(249,115,22,0.2)"
          }}
        >
          <Security sx={{ fontSize: 50, color: colors.orange.base }} />
        </Box>

        <Typography variant="h6" fontWeight={900} color="var(--fx-surface-text, #0F172A)" sx={{ mb: 1, lineHeight: 1.2 }}>
          High Security Asset
        </Typography>

        <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)" sx={{ mb: 2, fontWeight: 500 }}>
          Restricted access for:
        </Typography>

        <Typography 
          variant="body1" 
          fontWeight={800} 
          sx={{ 
            color: colors.orange.base, 
            bgcolor: "rgba(249,115,22,0.1)", 
            px: 2, py: 0.5, 
            borderRadius: "20px",
            fontSize: "0.85rem"
          }}
        >
          {itemTitle}
        </Typography>
      </Box>

      {/* PANEL DERECHO: Acción de Verificación */}
      <Box 
        sx={{ 
          width: { xs: "100%", sm: "60%" }, 
          p: 2, 
          display: "flex", 
          flexDirection: "column", 
          
        }}
      >
        <Box sx={{ mb: 4 ,mt:2 }}>
          <Typography variant="h5" fontWeight={900} color="var(--fx-surface-text, #0F172A)" sx={{textAlign:"center", mb: 3, fontSize:25 }}>
            Verification Required
          </Typography>
          <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)" sx={{ fontSize:14, lineHeight: 1.5 }}>
            Enter the 5-digit code we sent to: <br />
            <span style={{ color: "var(--fx-surface-text, #0F172A)", fontWeight: 700 }}>{emailMasked}</span>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", mb: 4 }}>
          {code.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputsRef.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isLoading}
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                style: {
                  textAlign: "center",
                  fontSize: 24, 
                  fontWeight: 800,
                  padding: "12px 0",
                  color: "var(--fx-surface-text, #0F172A)"
                },
              }}
              sx={{
                width: 55,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                  "& fieldset": { borderColor: "var(--fx-surface-borderstrong, #CBD5E1)" },
                  "&:hover fieldset": { borderColor: colors.orange.base },
                  "&.Mui-focused fieldset": { borderColor: colors.orange.base, borderWidth: 2 },
                },
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          fullWidth
          disabled={!isCodeComplete || isLoading}
          onClick={handleSubmit}
          sx={{
            backgroundColor: colors.orange.base,
            py: 1,
            mt:4,
            fontSize: "1rem",
            fontWeight: 800,
            borderRadius: "12px",
            textTransform: "none",
            boxShadow: "0 10px 15px -3px rgba(249,115,22,0.4)",
            "&:hover": { backgroundColor: colors.orange.base, boxShadow: "none" },
            mb: 3
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify & Access"}
        </Button>

        {typeof vaultRemainingSeconds === "number" && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Box 
              sx={{ 
                width: 8, height: 8, borderRadius: '50%', 
                bgcolor: vaultRemainingSeconds < 30 ? '#EF4444' : '#10B981',
                animation: 'pulse 2s infinite'
              }} 
            />
            <Typography variant="caption" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 700, letterSpacing: 0.5 }}>
              SESSION EXPIRES IN {formatMMSS(vaultRemainingSeconds)}
            </Typography>
          </Box>
        )}
      </Box>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>

      <SnackbarAlert
        openState={openAlert}
        setOpenState={setOpenAlert}
        severity={alertSeverity}
        message={alertMessage}
      />
    </Box>
  );
}

